import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ChapterCard } from "~/components/AssetModalV2/Content/Panel/components/ChapterCard";
import { ChapterVideoInfo } from "~/components/AssetModalV2/Content/Panel/components/ChapterVideoInfo";
import { useVideoContentControlsContext } from "~/components/AssetModalV2/Content/VideoContentControlsContextProvider";
import { useClipChapters } from "~/hooks/useClipChapters";
import { useVideoTimestampObserverContext } from "~/providers/VideoTimestampObserverProvider";
import { centralizedClipSelector } from "~/store/centralizedClip/selectors";
export const CHAPTERS_CONTAINER = "CHAPTERS_CONTAINER";
export const ChaptersPanel = /*#__PURE__*/ memo(()=>{
    const { chapters = [] } = useClipChapters();
    const clip = useSelector(centralizedClipSelector);
    const [activeChapter, setActiveChapter] = useState(null);
    const chapterRefs = useRef([]);
    const chaptersContainerRef = useRef(null);
    // Video controls for seeking
    const { setCurrentTime } = useVideoContentControlsContext();
    // Video timestamp observer for tracking current time
    const { subscribe, updateTime } = useVideoTimestampObserverContext();
    const findActiveChapter = useCallback((time)=>{
        // Find from end to start to prefer later chapters at boundary times
        for(let i = chapters.length - 1; i >= 0; i--){
            const chapter = chapters[i];
            if (time >= chapter.startTime && time <= chapter.endTime) {
                return chapter;
            }
        }
        return null;
    }, [
        chapters
    ]);
    // Subscribe to video timestamp updates to track active chapter
    useEffect(()=>{
        const subscription = subscribe((time)=>{
            if (time !== null) {
                const newActiveChapter = findActiveChapter(time);
                if (newActiveChapter !== activeChapter) {
                    setActiveChapter(newActiveChapter);
                }
            }
        });
        return ()=>{
            subscription.unsubscribe();
        };
    }, [
        subscribe,
        findActiveChapter,
        activeChapter
    ]);
    // Initialize chapter refs when chapters change
    useEffect(()=>{
        chapterRefs.current = chapters.map(()=>null);
    }, [
        chapters
    ]);
    const activeChapterIndex = activeChapter ? chapters.indexOf(activeChapter) : null;
    // Scroll active chapter into view when it changes
    useEffect(()=>{
        if (activeChapterIndex !== null && chaptersContainerRef.current) {
            const activeChapterElement = chapterRefs.current[activeChapterIndex];
            if (activeChapterElement) {
                const containerRect = chaptersContainerRef.current.getBoundingClientRect();
                const elementRect = activeChapterElement.getBoundingClientRect();
                const isVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
                if (!isVisible) {
                    activeChapterElement.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest"
                    });
                }
            }
        }
    }, [
        activeChapterIndex
    ]);
    // Seek to chapter functionality
    const seekToChapter = useCallback((chapter)=>{
        setCurrentTime(chapter.startTime);
        // Manually update the timestamp observer to ensure active chapter updates immediately
        // even when video is paused (since the time observer interval stops when paused)
        updateTime(chapter.startTime);
    }, [
        setCurrentTime,
        updateTime
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full flex-col",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "shrink-0 border-b border-grey-5 py-4",
                children: /*#__PURE__*/ _jsx(ChapterVideoInfo, {
                    chapters: chapters,
                    seekToChapter: seekToChapter,
                    activeChapterIndex: activeChapterIndex
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex min-h-0 flex-1 flex-col overflow-y-auto py-3",
                ref: chaptersContainerRef,
                "data-testid": CHAPTERS_CONTAINER,
                children: chapters.map((chapter, index)=>/*#__PURE__*/ _jsx(ChapterCard, {
                        chapter: chapter,
                        index: index,
                        isActive: index === activeChapterIndex,
                        onClick: ()=>seekToChapter(chapter),
                        clip: clip,
                        ref: (el)=>{
                            chapterRefs.current[index] = el;
                        }
                    }, index))
            })
        ]
    });
});
ChaptersPanel.displayName = "ChaptersPanel";
